import clsx from "clsx";
import parse, {
  HTMLReactParserOptions,
  Element,
  attributesToProps,
  domToReact,
  DOMNode,
} from "html-react-parser";
import escapeRegExp from "lodash/escapeRegExp";
import {
  FunctionComponent,
  HTMLProps,
  ElementType,
  type MouseEvent,
  useCallback,
  useMemo,
} from "react";

import AffiliateLink from "@ui/components/article/AffiliateLink";
import { SELECTORS } from "@ui/config";
import { useAffiliateContext } from "@ui/context/affilliate-context";
import { useConfigContext } from "@voxmedia/duet-context/context/config-context";
import { ONETRUST_CATEGORIES } from "@voxmedia/duet-context/context/cookie-consent-context";
import { processHtmlForCookieConsent } from "@voxmedia/duet-utils/cookie-consent";
import { URL_HTTP_PATTERN } from "@voxmedia/duet-utils/url";

/**
 * Returns html where absolute community links are transformed into
 * relative links.
 */
const makeCommunityLinksRelative = (html: string, communityOrigin: string) => {
  /** Regexp that matches text like `href="https://www.theverge.com` */
  const HREF_COMMUNITY_ORIGIN_REGEXP = new RegExp(
    `(href\\s*=\\s*['"])(${escapeRegExp(communityOrigin)})`,
    "gi",
  );

  return typeof html === "string"
    ? html.replace(HREF_COMMUNITY_ORIGIN_REGEXP, "$1")
    : html;
};

interface Props extends HTMLProps<HTMLElement> {
  component?: ElementType<any>;
  html: string;
  /** Whether to filter the html for embedded content to enforce cookie consent */
  filterForCookieConsent?: boolean;
  /** OneTrust category ID for embedded content to enforce cookie consent */
  cookieConsentCategoryId?: string;
  /**
   * When true, clicks inside an unrevealed `span.spoiler` set `data-revealed="true"`.
   * Clicks on links inside an unrevealed spoiler reveal first and do not navigate;
   * a second click follows the link. Used by Feed/Cyclone post bodies.
   */
  interactiveSpoilers?: boolean;
}

function createParseOptions(
  /** If provided, we can detect whether a url is an affiliate or not */
  isAffiliate?: ((url: string) => boolean) | null,
): HTMLReactParserOptions {
  const options: HTMLReactParserOptions = {
    replace: (domNode, index) => {
      const domElement = domNode as Element;
      // Replace anchors with `AffiliateLink` component.
      if (
        domElement.tagName === "a" &&
        (!isAffiliate ||
          // Check if link is an affiliate before replacing.
          (domElement.attribs["href"] &&
            isAffiliate(domElement.attribs["href"])))
      ) {
        const props = attributesToProps(domElement.attribs);
        const children = domToReact(domElement.children as DOMNode[], options);
        return (
          <AffiliateLink
            key={index}
            {...props}
            includeClickAnalytics="auto"
            includeViewAnalytics="auto"
          >
            {children}
          </AffiliateLink>
        );
      }
      return;
    },
  };
  return options;
}

/** Determine if `html` contains any affiliate links */
function hasAffiliateLinks(
  html: string,
  isAffiliate: (url: string) => boolean,
): boolean {
  const matches = html.matchAll(URL_HTTP_PATTERN);
  for (const match of matches) {
    if (isAffiliate(match[0])) {
      return true;
    }
  }
  return false;
}

const DangerouslySetCMSMarkup: FunctionComponent<Props> = ({
  html,
  component: Component = "p",
  className,
  filterForCookieConsent = false,
  cookieConsentCategoryId = ONETRUST_CATEGORIES.socialMediaAndEmbeds,
  interactiveSpoilers = false,
  onClick,
  ...rest
}) => {
  const affiliateContext = useAffiliateContext();
  const { communityOrigin } = useConfigContext().communityConfig;

  const __html = useMemo(() => {
    // Turn all https://www.theverge.com/ links relative
    const processedHtml = makeCommunityLinksRelative(html, communityOrigin);
    // Process the html for embedded content to enforce cookie consent
    if (filterForCookieConsent) {
      return processHtmlForCookieConsent(
        processedHtml,
        cookieConsentCategoryId,
      );
    }
    return processedHtml;
  }, [html, communityOrigin, cookieConsentCategoryId, filterForCookieConsent]);

  const handleClick = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      if (interactiveSpoilers && !e.defaultPrevented) {
        const target = e.target as HTMLElement | null;
        if (target) {
          const spoiler = target.closest("span.spoiler");
          if (
            spoiler instanceof HTMLElement &&
            spoiler.getAttribute("data-revealed") !== "true"
          ) {
            spoiler.setAttribute("data-revealed", "true");
            if (target.closest("a")) {
              e.preventDefault();
            }
          }
        }
      }
      onClick?.(e);
    },
    [interactiveSpoilers, onClick],
  );

  const props = {
    className: clsx(SELECTORS.DangerouslySetCMSMarkup, className),
    ...rest,
    ...(interactiveSpoilers || onClick ? { onClick: handleClick } : {}),
  };

  // Create transform html to React elements function.
  const transformOptions = useMemo(
    () => createParseOptions(affiliateContext?.isAffiliate),
    [affiliateContext?.isAffiliate],
  );

  // Determine if we need to transform any links.
  const shouldTransformLinks = useMemo(
    () =>
      affiliateContext?.isAffiliate
        ? // Search for affiliate links if we can.
          hasAffiliateLinks(__html, affiliateContext.isAffiliate)
        : true,
    [__html, affiliateContext?.isAffiliate],
  );

  if (filterForCookieConsent || !shouldTransformLinks) {
    // Render cookie-consent-filtered HTML directly so React does not treat blocked scripts as managed resources.
    // Also use this path when we do not need to transform affiliate links.
    return (
      <Component
        {...props}
        dangerouslySetInnerHTML={{ __html }}
        /** Suppress hydration warnings caused by OneTrust scripts */
        suppressHydrationWarning={filterForCookieConsent}
      />
    );
  }

  // Use html parse to transform affiliate links to `<AffiliateLink />`
  // Suppress hydration warnings caused by OneTrust scripts
  return (
    <Component {...props} suppressHydrationWarning={filterForCookieConsent}>
      {parse(__html, transformOptions)}
    </Component>
  );
};

export default DangerouslySetCMSMarkup;
